import React, { createContext, useState, useEffect, useContext } from 'react';
import 'core-js/actual/array/flat-map';
import { Environment } from 'src/Environment';

import { getTerminalFees, getOnboardingFees, getCardFees } from '../services/fee.service';
import { SalesforceContext } from './salesforce-context';
import { PricingTierType } from 'src/containers/onboarding/helpers/fees';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { isValidNumber } from 'src/helpers/validation';

const sessionStorageKeys = {
  terminalFeesKey: 'terminal_fees',
  onboardingFeesKey: 'onboarding_fees',
  cardFeesKey: 'card_fees',
};

const CARD_PRESENT_TEXT = 'Card Present';

export const FeesContext = createContext();

export function FeesProvider({ children }) {
  const { lead, terminals } = useContext(SalesforceContext);
  const [terminalFees, setTerminalFees] = useState(null);
  const [onboardingFees, setOnboardingFees] = useState(null);
  const [cardFees, setCardFees] = useState(null);
  const [isLoadingFromSessionStorage, setIsLoadingFromSessionStorage] = useState(true);
  const [isTerminalFeesCallFired, setIsTerminalFeesCallFired] = useState(false);
  const handleTechnicalError = useTechnicalErrorHandler();

  const fetchAndInitializeTerminalFees = () => {
    const optimisationEnabled = !Environment.current?.disabledFeatures?.optimiseFeesCall;
    return getTerminalFees(null, optimisationEnabled, handleTechnicalError).then((termFees) => {
      saveIfSetToSessionStorage(
        (value) =>
          sessionStorage.setItem(sessionStorageKeys.terminalFeesKey, JSON.stringify(value)),
        termFees,
      );
      setTerminalFees(termFees);
    });
  };

  const fetchAndInitializeOnboardingFeesAndCardFees = (monthlyCardTurnover) =>
    getOnboardingFees(null, handleTechnicalError)
      .then((onFees) => {
        return {
          userId: onFees.userId,
          acquiringProduct: onFees.acquiringProduct,
          onFees: onFees,
        };
      })
      .then(({ userId, acquiringProduct, onFees }) => {
        getCardFees(userId, monthlyCardTurnover, acquiringProduct, null, handleTechnicalError).then(
          (caFees) => {
            setCardFees(caFees);
            saveIfSetToSessionStorage(
              (value) =>
                sessionStorage.setItem(sessionStorageKeys.cardFeesKey, JSON.stringify(value)),
              caFees,
            );

            setOnboardingFees(onFees);
            saveIfSetToSessionStorage(
              (value) =>
                sessionStorage.setItem(sessionStorageKeys.onboardingFeesKey, JSON.stringify(value)),
              onFees,
            );
          },
        );
      });

  useEffect(() => {
    if (!isLoadingFromSessionStorage && !terminalFees && lead.email) {
      if (!isTerminalFeesCallFired) {
        setIsTerminalFeesCallFired(true);
        fetchAndInitializeTerminalFees();
      }
    }
  }, [isLoadingFromSessionStorage, lead.email]);

  useEffect(() => {
    if (
      !isLoadingFromSessionStorage &&
      isValidNumber(lead.monthlyCardTurnover) &&
      lead.monthlyCardTurnover >= 0 &&
      (!cardFees || !onboardingFees)
    ) {
      fetchAndInitializeOnboardingFeesAndCardFees(lead.monthlyCardTurnover);
    }
  }, [isLoadingFromSessionStorage, lead.monthlyCardTurnover]);

  const getAllCardFees = () => cardFees?.cardFeesData || [];

  const getVariableCardFees = () =>
    getAllCardFees().filter(
      (cardFee) =>
        cardFee?.pricingTierTypeName === PricingTierType.variable.name ||
        cardFee?.pricingTierTypeCode === PricingTierType.variable.code,
    );

  const getFixedCardFees = () =>
    getAllCardFees().filter(
      (cardFee) =>
        cardFee?.pricingTierTypeName === PricingTierType.fixed.name ||
        cardFee?.pricingTierTypeCode === PricingTierType.fixed.code,
    );

  const getOpportunityCardFees = (cardFees) =>
    Object.values(cardFees).map((cardFee) => ({
      percentage: cardFee.percentage,
      card_provider_name: cardFee.cardTypeBouquetName,
      terminal_type: cardFee.feeCategoryName === CARD_PRESENT_TEXT ? 'cp' : 'cnp',
      device_id: Object.values(terminals)[0].id,
    }));

  const feesContainFixed = () =>
    Environment.current?.disabledFeatures?.fixedFeesCheck ||
    getAllCardFees().some(
      (cardFee) =>
        cardFee?.pricingTierTypeName === PricingTierType.fixed.name &&
        cardFee?.pricingTierTypeCode === PricingTierType.fixed.code,
    );

  const getFilteredCardFees = (useFixedFees) =>
    Environment.current?.disabledFeatures?.fixedVariableTiers ||
    !useFixedFees ||
    !feesContainFixed()
      ? getVariableCardFees()
      : getFixedCardFees();

  const loadIfSetFromSessionStorage = (loader, key) => {
    const value = sessionStorage.getItem(key);
    if (value) {
      loader(value);
    }
  };

  const saveIfSetToSessionStorage = (saver, value) => {
    if (value) {
      saver(value);
    }
  };

  const loadFromSessionStorage = () => {
    loadIfSetFromSessionStorage(
      (value) => setTerminalFees(JSON.parse(value)),
      sessionStorageKeys.terminalFeesKey,
    );
    loadIfSetFromSessionStorage(
      (value) => setOnboardingFees(JSON.parse(value)),
      sessionStorageKeys.onboardingFeesKey,
    );
    loadIfSetFromSessionStorage(
      (value) => setCardFees(JSON.parse(value)),
      sessionStorageKeys.cardFeesKey,
    );
  };

  useEffect(() => {
    loadFromSessionStorage();
    setIsLoadingFromSessionStorage(false);
  }, []);

  const isAllFeesLoaded = () => cardFees && terminalFees && onboardingFees;

  return (
    <FeesContext.Provider
      value={{
        terminalFees,
        onboardingFees,
        cardFees,
        isAllFeesLoaded,
        getFilteredCardFees,
        getOpportunityCardFees,
      }}
    >
      {children}
    </FeesContext.Provider>
  );
}
